import type { AccessPreview } from 'features/permissions/types'
import { permissionsFromRoleNames } from 'features/permissions/utils/computePermissions'
import type { RolodexV3RolesAtScope } from 'features/permissions/utils/fetchRolodexV3RolesAtScope'
import {
  accessLevelOf,
  type ActiveScope,
  policyNameForAccess,
  type RolodexV3UserScope,
  scopeCovers,
} from 'features/permissions/utils/policyScopeEvaluation'
import { v3RoleNamesForScope } from 'features/permissions/utils/v3RoleNames'
import { ROLE_CANTILEVER_DOCUMENT_NAVIGATOR_VIEWER } from 'types'

/**
 * What the user holds, before it is checked against a scope. Each field is
 * undefined while it loads; a failed v3 load is passed as empty.
 */
export interface PermissionInputs {
  accessPreview: AccessPreview | null
  v2RoleNames: readonly string[] | undefined
  v3Roles: RolodexV3RolesAtScope | undefined
  accessScopes: readonly RolodexV3UserScope[] | undefined
}

/**
 * Permissions the user holds at the active scope: v2 role names, plus v3
 * roles and Manage Access levels whose grant covers that scope. Undefined
 * until every input and the active scope are known. A preview uses the
 * previewed user's role names as they are.
 */
export function computeScopedPermissions(
  inputs: PermissionInputs,
  active: ActiveScope | undefined
): ReadonlySet<string> | undefined {
  if (inputs.accessPreview) {
    return permissionsFromRoleNames(inputs.accessPreview.roleNames)
  }
  const { v2RoleNames, v3Roles, accessScopes } = inputs
  if (!active || !v2RoleNames || !v3Roles || !accessScopes) {
    return undefined
  }
  const accessPolicyNames = accessScopes
    .filter((scope) => scopeCovers(scope, active))
    .map((scope) => policyNameForAccess(accessLevelOf(scope)))
  return permissionsFromRoleNames(
    [
      ...v2RoleNames,
      ...v3RoleNamesForScope(v3Roles.roles, active),
      ...accessPolicyNames,
    ],
    v2RoleNames.includes(ROLE_CANTILEVER_DOCUMENT_NAVIGATOR_VIEWER) ||
      v3Roles.viewerAssignedAnywhere
  )
}
